// src/components/TourGuideModal.tsx
//
// Multi-step welcome tour shown once on the dashboard after signup.
// Persists dismissal via the existing onboardingCompletedSteps mechanism
// (PATCH /auth/onboarding/step body: { step: "tour_seen" }).

import React, { useEffect, useState } from "react";
import { axiosInstance } from "../utils/axiosConfig";

type Slide = {
  icon: string;
  title: string;
  body: string;
  highlights?: string[];
};

const SLIDES: Slide[] = [
  {
    icon: "🎯",
    title: "Welcome to Sumbitrbuddy",
    body:
      "Sumbitrbuddy is your end-to-end job-search companion. It surfaces matching roles, tracks every application, tailors your resume and cover letters, and keeps you on top of interviews — so the only thing you focus on is landing the job.",
    highlights: [
      "Find roles that actually match your profile",
      "Track every application in one place",
      "Get AI help on resumes and prep",
      "Never miss an interview or follow-up",
    ],
  },
  {
    icon: "🧭",
    title: "Your Dashboard at a glance",
    body:
      "The dashboard is your home base. From the left sidebar you can jump to any major area of the app. Your key stats (applications, recommendations, success rate) sit front and center, with quick-action cards underneath.",
    highlights: [
      "Sidebar = main navigation",
      "KPI cards = your job-search pulse",
      "Quick-action shortcuts to the most-used pages",
      "Coach messages, alerts, and reminders surface here",
    ],
  },
  {
    icon: "🔎",
    title: "Discover and save jobs",
    body:
      "Use Jobs to search across multiple boards (Indeed, LinkedIn, Adzuna, RemoteOK and more) at once. AI Matches automatically ranks postings against your profile, with a per-listing match score and breakdown.",
    highlights: [
      "Jobs page — manual search with filters",
      "AI Matches — algorithmic recommendations ranked by fit",
      "Saved Jobs — anything you bookmark with one click",
      "Region-aware: tell us where, we'll search the right boards",
    ],
  },
  {
    icon: "📋",
    title: "Track every application",
    body:
      "Mark a job as 'Applied' and Sumbitrbuddy starts tracking it. View your pipeline as a Kanban board, list, or timeline. Statuses transition cleanly: Applied → Interviewing → Offered → Accepted/Rejected.",
    highlights: [
      "Kanban / List / Timeline view modes",
      "AI auto-extracts skills, tools, and company info",
      "Hover any cell for detail; click for full metadata",
      "Email integration auto-updates statuses (Pro)",
    ],
  },
  {
    icon: "📄",
    title: "Resume & Cover Letter tools",
    body:
      "Upload your resume or build one from a template. Our clarity analyzer flags vague bullets, missing dates, and weak action verbs. Tailor any resume or cover letter to a specific job in seconds with AI suggestions grouped into Keyword / Reorder / Tone / Skills tabs.",
    highlights: [
      "Upload or template-build resumes",
      "Clarity analyzer with inline improvement hints",
      "AI tailoring per job (4 categorized suggestion tabs)",
      "Cover-letter builder + auto-tailor",
    ],
  },
  {
    icon: "🎤",
    title: "Interview prep & smart alerts",
    body:
      "When you mark an interview, Sumbitrbuddy schedules reminders 7 days, 1 day, 12 hours, and 1 hour ahead. The Interview Prep page gives you tailored questions, company research, and a checklist. Pro subscribers get extra morale boosts before each interview.",
    highlights: [
      "Multi-tier reminder cadence",
      "Tailored interview questions",
      "Pre-interview pep talks (Pro)",
      "Post-interview rating + reflection notes",
    ],
  },
  {
    icon: "📧",
    title: "Connect your email (optional)",
    body:
      "Link Gmail or Outlook (or any IMAP account) to have Sumbitrbuddy automatically detect application responses, parse interview invites, and update your tracker — no manual data entry needed.",
    highlights: [
      "Gmail / Outlook OAuth or any IMAP",
      "Auto-detects rejection, interview, and offer emails",
      "Updates application statuses automatically",
      "All credentials are encrypted at rest",
    ],
  },
  {
    icon: "📊",
    title: "Insights & community",
    body:
      "The Analytics page shows trends in your applications, response rates, and where you're spending time. The Community tab connects you with other job seekers, lets you start discussions, and build your network.",
    highlights: [
      "Funnels, KPIs, geo heatmap of applications",
      "Personalized insights and benchmarks",
      "Community discussions and connections",
      "Public profile (opt-in)",
    ],
  },
  {
    icon: "⚙️",
    title: "Settings, privacy, and plans",
    body:
      "Manage your profile, privacy and consent preferences, MFA, and subscription from Settings. Free users see ads and get core features; Pro unlocks AI tailoring, full email sync, advanced analytics, and the rich application table.",
    highlights: [
      "Granular privacy/consent controls",
      "Two-factor authentication (TOTP or SMS)",
      "Subscription management (daily/biweekly/monthly/annual)",
      "Notification cadence preferences",
    ],
  },
  {
    icon: "🚀",
    title: "You're ready!",
    body:
      "Start by finishing your profile (the banner up top will guide you), then head to AI Matches to see your first recommendations. Need help anytime? The Quick Tips panel and AI Assistant are one click away from your dashboard.",
  },
];

const TourGuideModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Check whether the user has already dismissed the tour
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/auth/onboarding/status")
      .then((r) => {
        if (cancelled) return;
        if (!r.data?.tourSeen) setOpen(true);
      })
      .catch(() => {
        /* if status call fails, don't pop the tour — fail closed */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const closeTour = async () => {
    setSaving(true);
    try {
      await axiosInstance.patch("/auth/onboarding/step", { step: "tour_seen" });
    } catch {
      /* non-fatal — user can always re-launch from Settings later */
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  if (!open) return null;

  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;
  const isFirst = step === 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => {
        // close-on-backdrop intentionally disabled — must use a button
        e.stopPropagation();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          maxWidth: 560,
          width: "100%",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with progress dots + skip */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #e2e8f0",
            background: "linear-gradient(135deg, #eff6ff 0%, #ede9fe 100%)",
          }}
        >
          <div style={{ display: "flex", gap: 6 }} aria-label={`Step ${step + 1} of ${SLIDES.length}`}>
            {SLIDES.map((_, i) => (
              <span
                key={i}
                aria-hidden="true"
                style={{
                  width: i === step ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === step ? "#1d4ed8" : i < step ? "#93c5fd" : "#cbd5e1",
                  transition: "all 0.2s ease",
                }}
              />
            ))}
          </div>
          <button
            onClick={closeTour}
            disabled={saving}
            style={{
              background: "transparent",
              border: "none",
              color: "#475569",
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? "wait" : "pointer",
              padding: 4,
            }}
          >
            Skip tour
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "32px 24px", overflowY: "auto", flex: 1, textAlign: "center" }}>
          <div
            style={{
              fontSize: 56,
              marginBottom: 16,
              lineHeight: 1,
            }}
            aria-hidden="true"
          >
            {slide.icon}
          </div>
          <h2 id="tour-title" style={{ margin: "0 0 12px", fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
            {slide.title}
          </h2>
          <p style={{ margin: "0 0 20px", color: "#475569", lineHeight: 1.6, fontSize: 15 }}>
            {slide.body}
          </p>
          {slide.highlights && slide.highlights.length > 0 && (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                textAlign: "left",
                display: "inline-block",
              }}
            >
              {slide.highlights.map((h, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    color: "#334155",
                    fontSize: 14,
                    marginBottom: 8,
                  }}
                >
                  <span aria-hidden="true" style={{ color: "#1d4ed8", flexShrink: 0, marginTop: 1 }}>
                    ✓
                  </span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer with nav */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 20px",
            borderTop: "1px solid #e2e8f0",
            background: "#f8fafc",
          }}
        >
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={isFirst}
            style={{
              background: "transparent",
              border: "1px solid #cbd5e1",
              color: isFirst ? "#cbd5e1" : "#475569",
              borderRadius: 6,
              padding: "8px 16px",
              fontWeight: 600,
              cursor: isFirst ? "not-allowed" : "pointer",
              fontSize: 14,
            }}
          >
            ← Back
          </button>
          <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>
            {step + 1} / {SLIDES.length}
          </span>
          {isLast ? (
            <button
              onClick={closeTour}
              disabled={saving}
              style={{
                background: "#16a34a",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "8px 20px",
                fontWeight: 700,
                cursor: saving ? "wait" : "pointer",
                fontSize: 14,
              }}
            >
              {saving ? "Saving..." : "Get started 🚀"}
            </button>
          ) : (
            <button
              onClick={() => setStep((s) => Math.min(SLIDES.length - 1, s + 1))}
              style={{
                background: "#1d4ed8",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "8px 20px",
                fontWeight: 700,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TourGuideModal;
